import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import Icon from '../../components/AppIcon';

const sections = [
  {
    id: 'getting-started',
    title: 'Getting Started',
    icon: 'Rocket',
    articles: [
      { title: 'Quick Start Guide', content: 'Welcome to Wilcart CRM! Follow these steps to get up and running quickly.\n\n**1. Complete Onboarding**\nAfter signing up, the onboarding wizard will guide you through setting up your organization, adding your first team members, and configuring your sales pipeline stages.\n\n**2. Import Your Data**\nGo to Accounts or Contacts and use the Import button to upload a CSV file with your existing customer data.\n\n**3. Set Up Your Pipeline**\nNavigate to Settings → Pipeline to customize your sales stages. Default stages include Lead, Qualified, Proposal, Negotiation, and Won/Lost.\n\n**4. Add Your First Deal**\nGo to Deals or Pipeline and click Add Deal. Fill in the deal name, value, associated account, and expected close date.\n\n**5. Invite Your Team**\nGo to Settings → Team to invite colleagues. They will receive an email invitation to join your organization.' },
      { title: 'System Requirements', content: 'Wilcart CRM is a web-based application that works in any modern browser.\n\n- Chrome 90+ (recommended)\n- Firefox 88+\n- Safari 14+\n- Edge 90+\n\nNo installation required. Access your CRM from any device with an internet connection.' }
    ]
  },
  {
    id: 'leads',
    title: 'Leads',
    icon: 'UserPlus',
    articles: [
      { title: 'Managing Leads', content: 'Leads are potential customers who have shown interest in your business but have not been qualified yet.\n\n**Adding a Lead**\nClick Add Lead on the Leads page. Fill in the contact information, source (where the lead came from), and any notes.\n\n**Lead Sources**\nTrack where your leads come from: Website, Referral, Social Media, Cold Outreach, Events, etc.\n\n**Converting a Lead**\nOnce a lead is qualified, you can convert them to a Contact and create an associated Deal.\n\n**Lead Status**\n- New: Just added, not yet contacted\n- Contacted: Initial outreach made\n- Qualified: Confirmed interest and budget\n- Converted: Moved to active deal\n- Lost: Not interested or unresponsive' }
    ]
  },
  {
    id: 'accounts',
    title: 'Accounts',
    icon: 'Building2',
    articles: [
      { title: 'Managing Accounts', content: 'Accounts represent the companies or organizations you do business with.\n\n**Account Types**\n- Client: Active paying customer\n- Prospect: Potential customer in your pipeline\n- Partner: Business partner or reseller\n- Vendor: Supplier or service provider\n\n**Account Fields**\nCompany Name, Industry, Website, Phone, Address, Owner, and associated Contacts and Deals.\n\n**Best Practices**\nAlways link contacts and deals to their parent account. This gives you a complete 360 degree view of each business relationship.' }
    ]
  },
  {
    id: 'contacts',
    title: 'Contacts',
    icon: 'Users',
    articles: [
      { title: 'Managing Contacts', content: 'Contacts are the individual people you interact with at your client companies.\n\n**Adding Contacts**\nClick Add Contact and fill in: First Name, Last Name, Email, Phone, Job Title, and the associated Account.\n\n**Contact Status**\n- Lead: Not yet a customer\n- Prospect: In active sales process\n- Customer: Active paying client\n- Inactive: No recent activity\n\n**Deleting Contacts**\nContacts can be deleted by hovering over a row in the table and clicking the trash icon. Deleted contacts are moved to Trash and can be recovered within 30 days.\n\n**Filtering Contacts**\nUse the filters to narrow down by Company, Role/Title, or Status. The search bar searches across name, email, and phone.' }
    ]
  },
  {
    id: 'deals',
    title: 'Deals',
    icon: 'Target',
    articles: [
      { title: 'Managing Deals', content: 'Deals represent potential sales transactions.\n\n**Deal Fields**\n- Name: Descriptive name for the deal\n- Value: Expected revenue in dollars\n- Stage: Current position in your pipeline\n- Close Date: Expected date to close\n- Priority: High / Medium / Low\n- Probability: Likelihood of winning (%)\n\n**Pipeline Stages**\nDeals move through stages as they progress. You can customize stages in Settings → Pipeline.\n\n**Pipeline View**\nSwitch to the Pipeline page for a Kanban board view. Drag and drop deals between stages.\n\n**Filtering Deals**\nFilter by Stage, Priority, Value Range, and Close Date to focus on what matters most.' }
    ]
  },
  {
    id: 'activities',
    title: 'Activities',
    icon: 'Activity',
    articles: [
      { title: 'Tracking Activities', content: 'Activities help you track all interactions and tasks related to your sales process.\n\n**Activity Types**\n- Call: Phone calls with prospects or clients\n- Email: Email communications\n- Meeting: In-person or video meetings\n- Task: To-do items and follow-ups\n- Note: General notes and observations\n\n**Auto-Logging**\nWhen you create a Lead, Contact, or Deal, an activity is automatically logged so you always have a complete history.\n\n**Activity Status**\n- Pending: Not yet done\n- Completed: Finished\n- Overdue: Past due date and not completed\n\n**Due Today**\nThe Dashboard shows activities due today so you never miss a follow-up.' }
    ]
  },
  {
    id: 'reports',
    title: 'Reports',
    icon: 'BarChart3',
    articles: [
      { title: 'Understanding Reports', content: 'The Reports page gives you insights into your sales performance.\n\n**Key Metrics**\n- Conversion Rate: Percentage of deals that are won\n- Average Deal Size: Mean value of all deals\n- Win Rate: Won deals vs total closed deals\n- Total Deals: All deals in your pipeline\n\n**Charts**\n- Revenue Chart: Revenue trend over time\n- Conversion Funnel: How deals progress through stages\n- Win Rate Chart: Win/loss breakdown\n\n**Filters**\nFilter reports by date range to analyze specific time periods. Use the export button to download data as CSV.' }
    ]
  },
  {
    id: 'ai-features',
    title: 'AI Features',
    icon: 'Zap',
    articles: [
      { title: 'AI Voice', content: 'AI Voice allows you to make and manage sales calls with AI assistance.\n\n**Features**\n- Automated call scripts tailored to your business\n- Call recording and transcription\n- AI-powered call insights and sentiment analysis\n- Follow-up task suggestions based on call outcomes\n\nAvailable on CRM + AI Voice plan and above.' },
      { title: 'AI Marketing', content: 'AI Marketing helps you create and manage targeted marketing campaigns.\n\n**Features**\n- AI-generated email templates\n- Customer segmentation\n- Campaign analytics\n- Automated follow-up sequences\n\nAvailable on CRM + AI Voice + Website Builder plan and above.' },
      { title: 'Ask AI Assistant', content: 'The Ask AI button (floating button in the bottom right) gives you instant access to an AI assistant.\n\n**What you can ask:**\n- How many open deals do I have?\n- Summarize my top accounts\n- What activities are overdue?\n- Draft a follow-up email for a contact\n\nThe AI assistant uses your actual CRM data to provide relevant, personalized answers.' }
    ]
  },
  {
    id: 'settings',
    title: 'Settings',
    icon: 'Settings',
    articles: [
      { title: 'Configuring Your CRM', content: 'Access Settings from the sidebar to configure your CRM.\n\n**Profile**\nUpdate your name, email, and password.\n\n**Company**\nSet your organization name, logo, and business details.\n\n**Pipeline**\nCustomize your sales pipeline stages. Add, rename, reorder, or delete stages to match your sales process.\n\n**Team**\nInvite team members and manage their roles and permissions.\n\n**Custom Fields**\nAdd custom fields to Contacts, Accounts, or Deals to capture business-specific information.' }
    ]
  }
];

const Documentation = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('getting-started');
  const [activeArticle, setActiveArticle] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const navigate = useNavigate();

  const handleMenuToggle = () => setIsSidebarOpen(!isSidebarOpen);
  const handleSidebarClose = () => setIsSidebarOpen(false);

  const currentSection = sections?.find(s => s?.id === activeSection);
  const currentArticle = currentSection?.articles?.[activeArticle];

  const filteredSections = searchTerm
    ? sections?.map(s => ({
        ...s,
        articles: s?.articles?.filter(a =>
          a?.title?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
          a?.content?.toLowerCase()?.includes(searchTerm?.toLowerCase())
        )
      }))?.filter(s => s?.articles?.length > 0)
    : sections;

  const renderContent = (content) => {
    return content?.split('\n')?.map((line, i) => {
      if (line?.startsWith('**') && line?.endsWith('**')) {
        return <h4 key={i} className="font-semibold text-foreground mt-4 mb-2">{line?.replace(/\*\*/g, '')}</h4>;
      }
      if (line?.startsWith('- ')) {
        return (
          <div key={i} className="flex items-start space-x-2 mb-1">
            <span className="text-muted-foreground mt-1">•</span>
            <span className="text-sm text-muted-foreground">{line?.replace('- ', '')}</span>
          </div>
        );
      }
      if (line?.trim() === '') return <div key={i} className="h-2" />;
      return <p key={i} className="text-sm text-foreground leading-relaxed">{line}</p>;
    });
  };

  return (
    <>
      <Helmet>
        <title>Documentation - Wilcart CRM</title>
        <meta name="description" content="Wilcart CRM documentation and user guides." />
      </Helmet>
      <div className="min-h-screen bg-background">
        <Header onMenuToggle={handleMenuToggle} isSidebarOpen={isSidebarOpen} />
        <Sidebar isOpen={isSidebarOpen} onClose={handleSidebarClose} />
        <main className="lg:ml-64 pt-24 lg:pt-28">
          <div className="p-4 lg:p-6">
            <div className="mb-6">
              <div className="flex items-center space-x-3 mb-2">
                <div className="w-10 h-10 bg-primary/10 rounded-xl flex items-center justify-center">
                  <Icon name="BookOpen" size={20} className="text-primary" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-foreground">Documentation</h1>
                  <p className="text-muted-foreground text-sm">Everything you need to know about Wilcart CRM</p>
                </div>
              </div>
            </div>

            <div className="flex flex-col lg:flex-row gap-6">
              <div className="lg:w-64 flex-shrink-0">
                <div className="relative mb-4">
                  <Icon name="Search" size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <input
                    type="text"
                    placeholder="Search docs..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e?.target?.value)}
                    className="w-full pl-9 pr-4 py-2 bg-background border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>

                <nav className="bg-card border border-border rounded-xl overflow-hidden">
                  {filteredSections?.map((section) => (
                    <div key={section?.id}>
                      <button
                        onClick={() => { setActiveSection(section?.id); setActiveArticle(0); }}
                        className={`w-full flex items-center space-x-3 px-4 py-3 text-sm font-medium transition-colors text-left border-b border-border last:border-b-0 ${
                          activeSection === section?.id ? 'bg-primary/10 text-primary' : 'text-foreground hover:bg-muted/50'
                        }`}
                      >
                        <Icon name={section?.icon} size={16} />
                        <span>{section?.title}</span>
                        <span className="ml-auto text-xs text-muted-foreground">{section?.articles?.length}</span>
                      </button>
                      {activeSection === section?.id && section?.articles?.length > 1 && (
                        <div className="bg-muted/30">
                          {section?.articles?.map((article, idx) => (
                            <button
                              key={idx}
                              onClick={() => setActiveArticle(idx)}
                              className={`w-full text-left px-8 py-2 text-sm transition-colors ${
                                activeArticle === idx ? 'text-primary font-medium' : 'text-muted-foreground hover:text-foreground'
                              }`}
                            >
                              {article?.title}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </nav>

                <div className="mt-4 bg-primary/5 border border-primary/20 rounded-xl p-4">
                  <div className="flex items-center space-x-2 mb-2">
                    <Icon name="HelpCircle" size={18} className="text-primary" />
                    <span className="text-sm font-semibold text-foreground">Need Help?</span>
                  </div>
                  <p className="text-xs text-muted-foreground mb-3">Our support team is here to help.</p>
                  <button onClick={() => navigate('/contact-support')} className="w-full text-center text-xs font-medium text-primary hover:underline">
                    Contact Support →
                  </button>
                </div>
              </div>

              <div className="flex-1 min-w-0">
                {currentArticle ? (
                  <div className="bg-card border border-border rounded-xl p-6 lg:p-8">
                    <div className="flex items-center space-x-2 text-sm text-muted-foreground mb-6">
                      <Icon name="BookOpen" size={14} />
                      <span>{currentSection?.title}</span>
                      <Icon name="ChevronRight" size={14} />
                      <span className="text-foreground font-medium">{currentArticle?.title}</span>
                    </div>
                    <h2 className="text-xl font-bold text-foreground mb-6">{currentArticle?.title}</h2>
                    <div className="space-y-1">{renderContent(currentArticle?.content)}</div>
                    <div className="flex items-center justify-between mt-8 pt-6 border-t border-border">
                      <button
                        onClick={() => { if (activeArticle > 0) setActiveArticle(activeArticle - 1); }}
                        disabled={activeArticle === 0}
                        className="flex items-center space-x-2 text-sm text-muted-foreground hover:text-foreground disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        <Icon name="ChevronLeft" size={16} />
                        <span>Previous</span>
                      </button>
                      <button
                        onClick={() => { if (activeArticle < (currentSection?.articles?.length || 0) - 1) setActiveArticle(activeArticle + 1); }}
                        disabled={activeArticle >= (currentSection?.articles?.length || 0) - 1}
                        className="flex items-center space-x-2 text-sm text-muted-foreground hover:text-foreground disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        <span>Next</span>
                        <Icon name="ChevronRight" size={16} />
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="bg-card border border-border rounded-xl p-8 text-center">
                    <Icon name="Search" size={40} className="text-muted-foreground mx-auto mb-3" />
                    <p className="text-muted-foreground">No articles found for "{searchTerm}"</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </main>
      </div>
    </>
  );
};

export default Documentation;
